import React from 'react';

import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import ClickAwayListener from '~/components/core/Atomic/ClickAwayListener';
import Text from '~/components/core/TextComponents/Text';
import { ArrowElbowRightIcon } from '~/components/icons';
import cn from '~/Utils/cn';

import CardMeta, { CardTime } from '../../CardMeta';
import { KIND_APPEARANCE } from '../../constants';
import type { ActionStateAppearance, ActivityKind } from '../../types';
import { formatEntryDateTime } from '../../utils';

/** The frame, in the colours of the state the thing is in. */
type CardAppearance = Pick<ActionStateAppearance, 'card'>;

interface ActivityCardProps {
  kind: ActivityKind;
  appearance: CardAppearance;
  /** The thing itself, in words. Never the kind — the icon says that. */
  title: string;
  /** The exposure, where there is one. */
  subtitle?: string;
  /** The state the thing is in, on its own line over the title. */
  status: React.ReactNode;
  /** How sure Clive was of the thing, beside its state, where it sent a score. */
  confidence?: React.ReactNode;
  /** When Clive raised the thing: its `created_at`, as the API sent it. */
  createdAt: Date | string;
  /** The controls, outside the title's click overlay. */
  footer?: React.ReactNode;
  /** The body between the title and the controls. */
  children?: React.ReactNode;
  /** Opens the card's entry in the log: from the whole card, and from "Show in the log". */
  onSelect?: () => void;
  /** The DOM id of the log entry `onSelect` opens, named on both controls as what they control. */
  controls?: string;
  /** What the ↳ control is called: what the reader goes to the log to do. */
  selectLabel?: string;
  /** The card's DOM id, which the log's controls that show this card name. */
  elementId: string;
  /** Rings the card: the log last took the reader here. */
  isSelected: boolean;
  /** Unrings the card, on a click outside it. */
  onDeselect: () => void;
}

/**
 * The geometry every card in this panel shares.
 *
 * Recommendations, requests and questions are the same *shape* of thing — Clive raised something,
 * it has a state, and it is or is not waiting on somebody — and differ only in who does the work.
 * Three unrelated layouts made a handler learn three cards to answer one question, so the kind
 * changes the icon, the colour, the state under the title and the footer's corners, and nothing else.
 */
const ActivityCard: React.FC<ActivityCardProps> = ({
  kind,
  appearance,
  title,
  subtitle,
  status,
  confidence,
  createdAt,
  footer,
  children,
  onSelect,
  controls,
  selectLabel = 'Show in the log',
  elementId,
  isSelected,
  onDeselect,
}) => {
  const { Icon, chip, iconColor } = KIND_APPEARANCE[kind];
  const time = formatEntryDateTime(createdAt);

  return (
    // Only the ringed card listens, as only the selected log entry does.
    <ClickAwayListener
      onClickAway={(event) => {
        // The log's control for this card brings the reader here, so a click on it keeps the ring.
        const control = `[aria-controls="${elementId}"]`;
        if (!(event.target instanceof Element && event.target.closest(control))) onDeselect();
      }}
      mouseEvent={isSelected ? 'onClick' : false}
      touchEvent={isSelected ? 'onTouchEnd' : false}
    >
      <li id={elementId} aria-current={isSelected || undefined} className="flex list-none flex-col gap-4">
        <CardTime time={time} />

        <div
          className={cn('rounded-lg border-1 border-solid bg-white p-16', appearance.card, {
            'hover:shadow-md': onSelect,
            'outline outline-2 outline-offset-4 outline-blue-600': isSelected,
          })}
        >
          <div className="relative">
            <div className="mb-8">
              <CardMeta status={status} confidence={confidence} />
            </div>

            <div className="flex items-start gap-12">
              <span className={cn('grid h-32 w-32 shrink-0 place-items-center rounded-lg', chip)}>
                <Icon size={18} iconColor={iconColor} />
              </span>

              <div className="min-w-0 flex-1">
                {/* The title line is the icon's height, so the icon and a one-line title share a
                midline. The title has the column to itself: the status sits on a line of its own
                above, where a long one cannot narrow the title to a few words a line. */}
                <div className="flex min-h-32 items-center">
                  {onSelect ? (
                    // The title is the control, and its `after:` overlay stretches the click over the header
                    // and body. The footer sits outside that block, so its buttons stay clickable — a button
                    // around the card could not hold the card's own buttons.
                    <button
                      type="button"
                      onClick={onSelect}
                      aria-controls={controls}
                      className="cursor-pointer border-0 bg-transparent p-0 text-left after:absolute after:inset-0"
                    >
                      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="wrap-anywhere">
                        {title}
                      </Text>
                    </button>
                  ) : (
                    <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="wrap-anywhere">
                      {title}
                    </Text>
                  )}
                </div>
                {subtitle ? (
                  // Never cut short: it wraps to the column, as the title does.
                  <Text
                    variant={Text.VARIANTS.XS}
                    weight={Text.WEIGHTS.REGULAR}
                    colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                    className="wrap-anywhere"
                  >
                    {subtitle}
                  </Text>
                ) : null}
              </div>
            </div>

            {children ? <div className="mt-12 flex flex-col gap-8">{children}</div> : null}
          </div>

          {footer || onSelect ? (
            <div className="mt-12 flex items-center gap-8">
              <div className="min-w-0 flex-1">{footer}</div>
              {onSelect ? (
                // The same jump as the whole-card click, said out loud, reachable by keyboard and by
                // name. The footer sits outside the title's overlay, so it needs no lift above it.
                <IconButton
                  size="small"
                  onClick={onSelect}
                  title={selectLabel}
                  aria-label={selectLabel}
                  aria-controls={controls}
                >
                  <ArrowElbowRightIcon size={16} />
                </IconButton>
              ) : null}
            </div>
          ) : null}
        </div>
      </li>
    </ClickAwayListener>
  );
};

export default ActivityCard;
